import React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarDays, PackageSearch } from 'lucide-react';
import { Alert, Button, EmptyState, PageHeader, StatusPill, Timeline, WhatsAppIcon } from '@/components/common';
import { useReturnCase } from '@/hooks/useReturns';
import { whatsappUrl } from '@/lib/support';
import type { SubmittedReturn } from '@/types/returns';
import { comingSoon } from '@/utils/orderPresentation';
import { RETURN_STATUS_BADGE } from '@/utils/returnCases';

const COLUMN = 'tw-mx-auto tw-flex tw-w-full tw-max-w-[680px] tw-flex-col tw-gap-5 md:tw-gap-6';

/** R7A–R7C Return requested and R8 Pickup failed: where a return stands and what to do next. */
const ReturnStatusPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const submitted = useLocation().state as SubmittedReturn | undefined;
  const { returnCase, loading, error, reload } = useReturnCase(id, params.get('sample'), submitted ?? undefined);
  const orderPath = `/customer/orders/${id}`;
  const back = { to: orderPath, label: 'Order details' };

  if (loading || error || !returnCase) {
    return (
      <div className={COLUMN}>
        <PageHeader title="Return" back={back} />
        {loading ? (
          <div aria-busy className="tw-h-64 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Alert tone="danger" title="Something went wrong">
              {error}
            </Alert>
            <Button variant="secondary" className="tw-self-start" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : (
          <EmptyState
            icon={PackageSearch}
            title="No return on this order"
            description="Returns you start on this order show up here."
            action={<Button onClick={() => navigate(orderPath)}>Back to order</Button>}
          />
        )}
      </div>
    );
  }

  const { status, label, steps } = returnCase;
  const { tone, label: badge } = RETURN_STATUS_BADGE[status];
  const awaiting = status === 'awaiting_approval';
  const failed = status === 'pickup_failed';

  return (
    <div className={COLUMN}>
      <PageHeader
        title={awaiting ? 'Return requested' : `Return ${label}`}
        badge={<StatusPill tone={tone}>{badge}</StatusPill>}
        subtitle={awaiting ? [label, returnCase.resolutionSummary].filter(Boolean).join(' · ') : returnCase.productTitle}
        back={back}
      />

      {failed && (
        <Alert tone="danger" title="Pickup couldn’t be completed">
          {returnCase.pickupFailure}
        </Alert>
      )}

      <section aria-labelledby="return-status" className="tw-flex tw-flex-col tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
        <h2 id="return-status" className="tw-text-[17px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary">
          Return status
        </h2>
        <Timeline steps={steps} />
      </section>

      <div className="tw-flex tw-flex-col tw-gap-2.5 sm:tw-flex-row">
        {failed ? (
          <>
            <Button leadingIcon={CalendarDays} className="sm:tw-flex-1" onClick={() => comingSoon('Rescheduling a pickup')}>
              Reschedule pickup
            </Button>
            <Button
              variant="secondary"
              leadingIcon={WhatsAppIcon}
              className="sm:tw-flex-1"
              href={whatsappUrl(`Hi, the pickup for my return ${label} failed. Can you help?`)}
              target="_blank"
            >
              Chat with support
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" className="sm:tw-flex-1" onClick={() => navigate(orderPath)}>
              Back to order
            </Button>
            {awaiting && (
              <Button variant="danger" className="sm:tw-flex-1" onClick={() => comingSoon('Cancelling a return')}>
                Cancel return
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReturnStatusPage;
